import React, { createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toEnPath, toNlPath } from "./slugs";

const dict = {
  en: {
    nav: { home: "Home", services: "Services", how: "How It Works", pricing: "Pricing", industries: "Industries", about: "About", dashboard: "Live Demo Dashboard", contact: "Contact", bookDemo: "Plan a Free Analysis" },
    navMenus: {
      services: [
        { label: "Missed-Call Recovery", to: "/services#missed-call-recovery" },
        { label: "AI Customer Replies", to: "/services#ai-customer-replies" },
        { label: "Lead Intake", to: "/services#lead-intake" },
        { label: "Booking & Reservation Flow", to: "/services#booking-flow" },
        { label: "Human Takeover", to: "/services#human-takeover" },
      ],
      industries: [
        { label: "Restaurants", to: "/industries#restaurants" },
        { label: "Plumbers & Home Services", to: "/industries#plumbers-home-services" },
        { label: "Barbers & Salons", to: "/industries#barbers-salons" },
        { label: "Local Service Businesses", to: "/industries#local-service-businesses" },
      ],
    },
    clients: { title: "Trusted by businesses that value every lead" },
    mascot: { text: "Missed calls can mean missed customers. Discover what your business could recover." },
    og: {
      title: "TP Agency | Prevent Losing Customers to Missed Calls",
      desc: "Instant SMS follow-up and AI-powered customer support for service businesses.",
    },
    notFound: {
      title: "Page not found | TP Agency",
      heading: "Page not found",
      sub: "This page doesn't exist or has been moved.",
      back: "Back to home",
    },
    seo: {
      home: { title: "TP Agency — AI Missed-Call Recovery for Local Businesses", desc: "TP Agency helps local businesses respond to missed calls within seconds using SMS and AI, so fewer leads go to competitors. Plan a free analysis." },
      services: { title: "Services — Missed-Call Recovery, AI Replies & Lead Intake | TP Agency", desc: "Missed-call recovery, AI customer replies, lead intake, booking flows and human takeover — automation systems built around your business." },
      how: { title: "How It Works — Missed-Call Recovery in 5 Steps | TP Agency", desc: "See how TP Agency detects missed calls, replies by SMS within seconds, collects details with AI and notifies your team when it matters." },
      pricing: { title: "Pricing — AI Missed-Call Recovery Packages | TP Agency", desc: "Simple monthly packages from €199. Basic missed-call recovery, Pro AI receptionist and Premium full AI front desk. Prices excl. VAT." },
      industries: { title: "Industries — Restaurants, Plumbers, Salons & More | TP Agency", desc: "TP Agency adapts to restaurants, plumbers, barbers, salons and local service businesses — tuned to your customer flow and tone of voice." },
      about: { title: "About TP Agency — Faster Replies for Local Businesses", desc: "TP Agency helps businesses respond faster and prevent missed calls from becoming lost customers. Learn about our approach." },
      contact: { title: "Contact TP Agency — Plan a Free Analysis", desc: "Plan a free analysis or send us a message. We'll show you how much revenue may be recoverable from missed calls." },
      dashboard: { title: "Live Demo Dashboard | TP Agency", desc: "Explore a demo dashboard with example data: recovered calls, leads, conversations, appointments and estimated revenue." },
      privacy: { title: "Privacy Policy | TP Agency", desc: "How TP Agency handles personal data collected through this website, in line with the GDPR." },
      terms: { title: "Terms and Conditions | TP Agency", desc: "The terms and conditions that apply to the website and services of TP Agency." },
    },
    hero: {
      eyebrow: "AI Customer Support · Missed-Call Recovery",
      title1: "Prevent Losing Customers",
      title2: "to Missed Calls",
      sub: "TP Agency helps businesses respond faster, recover missed leads, and automate customer replies with SMS and AI-powered systems tailored to their business flow.",
      cta1: "Plan a Free Analysis",
      cta2: "Contact Us",
      trust: "Built for restaurants, plumbers, electricians, HVAC and service businesses.",
      flow: {
        missed: "Missed call", missedSub: "+31 6 12 34 56 78 · 14:02",
        ai: "AI SMS sent", aiSub: "Hi! We missed your call — how can we help?",
        reply: "Customer reply", replySub: "Burst pipe in kitchen — need urgent help",
        booked: "Appointment request captured", bookedSub: "Today · 15:30 · Emergency call-out",
        time: "Time to recovery",
      },
    },
    problem: {
      eyebrow: "The Problem",
      title: "Every Missed Call is a Customer Choosing Your Competitor",
      sub: "Local businesses lose thousands every month simply because someone couldn't answer the phone in time.",
      points: [
        { stat: "62%", label: "of callers never leave a voicemail — they call the next business on Google." },
        { stat: "8 sec", label: "is all the average customer waits for a reply before moving on." },
        { stat: "€3,000+", label: "in lost monthly revenue for a typical local service business." },
        { stat: "24/7", label: "customers now expect a reply — even at 11pm on a Sunday." },
      ],
    },
    how: {
      eyebrow: "How It Works",
      title: "A Simple, Reliable System That Runs In The Background",
      sub: "You keep doing your job. We help recover missed leads and answer customer questions.",
      stepLabel: "STEP",
      steps: [
        { n: "01", title: "Missed call detected", desc: "The moment a call is missed, our system triggers an automatic SMS to the caller within seconds." },
        { n: "02", title: "AI conversation starts", desc: "Your AI assistant — tailored to the specific needs of your business and customer flow — greets the customer and understands what they need." },
        { n: "03", title: "Questions answered · info collected", desc: "Opening hours, menu, prices, service details, reservations, job details — all handled instantly." },
        { n: "04", title: "You get notified when it matters", desc: "Human takeover rules alert you only for booked jobs, reservations or urgent leads." },
      ],
    },
    ai: {
      eyebrow: "AI Capabilities",
      title: "What Your AI Assistant Can Answer",
      sub: "Trained on the details of your business, ready in days, always polite and on-brand.",
      list: [
        { title: "Company Information", desc: "About your business, team, service area, story and USPs." },
        { title: "Opening Hours", desc: "Today's hours, holiday schedules, emergency availability." },
        { title: "Menu & Services", desc: "Dishes, specials, service list, pricing, current promotions." },
        { title: "Reservations & Bookings", desc: "Collect reservation and appointment details following your rules." },
        { title: "FAQs", desc: "Any recurring question your customers ask, answered instantly." },
        { title: "Useful Links", desc: "Menu PDFs, delivery, ordering, booking or payment links on demand." },
        { title: "Allergy & Safety", desc: "Allergens, safety procedures, insurance details, warranties." },
        { title: "Job & Lead Intake", desc: "Address, urgency, problem description, photos — routed to you." },
        { title: "Human Handover", desc: "Custom rules decide when to escalate to you or your team." },
      ],
    },
    packages: {
      eyebrow: "Packages",
      title: "Pricing Based On Service Level, Not Business Type",
      sub: "Some businesses only need basic missed-call recovery. Others need advanced AI lead intake, reservation requests, job qualification, human takeover rules and reporting. All plans use \"From\" pricing — final quote depends on complexity.",
      billing: "per month",
      fromLabel: "From",
      vat: "excl. VAT",
      cta: "Plan a Free Analysis",
      list: [
        {
          name: "Basic — Missed Call Recovery",
          price: "€199",
          badge: null,
          bestFor: "Restaurants, barbers, salons and small local businesses that need simple missed-call follow-up.",
          desc: "For businesses that want to automatically reply when they miss a call and help customers with basic questions.",
          features: [
            "Missed-call SMS",
            "Basic AI SMS assistant",
            "Opening hours",
            "General business questions",
            "Basic FAQ",
            "1 phone number",
            "Simple lead inbox",
            "Basic setup and testing",
          ],
        },
        {
          name: "Pro — AI Receptionist / Lead Intake",
          price: "€299",
          badge: "Most Popular",
          bestFor: "Restaurants with reservations and plumbers/service businesses that need more detailed customer intake.",
          desc: "For businesses that want AI to answer questions, collect reservation requests or job details, and notify the owner when human follow-up is needed.",
          features: [
            "Everything in Basic",
            "Advanced AI knowledge base",
            "Menu or service questions",
            "Reservation request or job-intake questions",
            "Human takeover rules",
            "Owner notifications",
            "Pipeline setup",
            "Basic calendar / request setup",
            "Monthly optimization",
          ],
        },
        {
          name: "Premium — Full AI Front Desk",
          price: "€449",
          badge: null,
          bestFor: "Busy restaurants, plumbers and service businesses with higher lead volume or more complex workflows.",
          desc: "For businesses that want a more complete AI front desk system with reporting, follow-ups and advanced automations.",
          features: [
            "Everything in Pro",
            "Advanced workflows",
            "Review automation",
            "Reporting dashboard",
            "Follow-up automation",
            "Multiple AI flows",
            "Priority support",
            "Monthly performance review",
          ],
        },
      ],
      setup: "Prices are shown in EUR and exclude VAT. TP Agency currently focuses on Dutch and European businesses.",
      note: "Pricing depends on business type, call volume, SMS usage, AI complexity and the level of automation needed. Plumbers and service businesses usually require lead qualification, urgency detection and location-based intake, which are included from the Pro package.",
    },
    restoDemo: {
      eyebrow: "Restaurant Demo",
      title: "How It Works For A Restaurant",
      sub: "Real Friday-evening scenario — the phone rings while the team is serving tables.",
      businessName: "Trattoria Bella · Amsterdam",
      messages: [
        { from: "system", text: "📞 Missed call from +31 6 21 88 44 12 · 19:34" },
        { from: "ai", t: "19:34", text: "Hi! Sorry we couldn't answer — this is Bella's AI assistant. How can I help?" },
        { from: "user", t: "19:34", text: "Do you have a table for 4 tonight around 20:30?" },
        { from: "ai", t: "19:34", text: "I'd be happy to pass your reservation request for 4 at 20:30 to the team. Any allergies or dietary needs I should note?" },
        { from: "user", t: "19:35", text: "One vegetarian, one gluten-free please." },
        { from: "ai", t: "19:35", text: "Noted. Can I have the name for the request and a mobile number so the team can confirm?" },
        { from: "user", t: "19:35", text: "Julia — 06 21 88 44 12" },
        { from: "ai", t: "19:36", text: "✅ Request captured, Julia — Friday 20:30 · 4 guests · veg + GF. The team will confirm by SMS shortly!" },
      ],
    },
    plumberDemo: {
      eyebrow: "Plumber Demo",
      title: "How It Works For A Plumber",
      sub: "Emergency call recovered in under a minute — job intake handled automatically.",
      businessName: "PipePro Plumbing · Rotterdam",
      messages: [
        { from: "system", t: "14:02", text: "📞 Missed call from +31 6 12 34 56 78" },
        { from: "ai", t: "14:02", text: "Hi! This is Mike from PipePro Plumbing. Sorry we missed your call — what's going on?" },
        { from: "user", t: "14:03", text: "I have a burst pipe under my kitchen sink, water everywhere!" },
        { from: "ai", t: "14:03", text: "That sounds urgent. Can you turn off the main shut-off valve? And what's your postal code?" },
        { from: "user", t: "14:04", text: "Yes shut it off. Postal code 1011AB Amsterdam." },
        { from: "ai", t: "14:04", text: "Got it. I'll forward this to Mike as an emergency request. Do you have a preferred time slot today?" },
        { from: "user", t: "14:05", text: "As soon as possible — early afternoon works." },
        { from: "ai", t: "14:05", text: "✅ Emergency slot request forwarded to Mike — preferred time: early afternoon. He'll confirm availability by SMS shortly. Stay safe." },
      ],
    },
    demo: {
      step1: "Missed Call", step2: "AI SMS Sent", step3: "Customer Replies", step4: "AI Qualifies", step5: "Request Forwarded",
      aiStatus: "AI assistant · auto-replying",
    },
    why: {
      eyebrow: "Why TP Agency",
      title: "Built For Businesses That Want to Respond Faster",
      sub: "Simple systems, clear workflows, and AI-powered automation that helps your team capture missed leads, reply faster, stay organized, and be prepared for every customer interaction. Flexible options available depending on your business needs. Clear packages, simple onboarding, and support that fits your business stage.",
      points: [
        { title: "Set up in days, not months", desc: "We train your AI on your business, launch fast, and refine live with you." },
        { title: "Tailored to your business", desc: "Not a generic chatbot — every reply is set up specifically for your business type and customer flow." },
        { title: "Human takeover when it matters", desc: "You define exactly when the AI must alert a real person." },
        { title: "Works while you sleep", desc: "24/7 replies to missed calls and messages, evenings, weekends, holidays." },
        { title: "GDPR-friendly & Dutch-first", desc: "EU-hosted, GDPR-compliant, native Dutch + English support." },
        { title: "Pays for itself", desc: "A single recovered job usually covers a full month of subscription." },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Answers To The Most Common Questions",
      list: [
        { q: "How long does setup take?", a: "Most businesses are live within 3–7 business days. We handle the training on your info, menu or service catalogue, and connect it to your phone number." },
        { q: "Do I need to change my phone number?", a: "No. We keep your existing number. Missed calls are detected via call-forwarding or your telecom's missed-call event — nothing changes for your callers." },
        { q: "What languages does the AI speak?", a: "Native Dutch and English out of the box. German, French, Spanish and Italian available on the Pro plan." },
        { q: "What happens when the AI doesn't know an answer?", a: "It follows your human-takeover rules: it politely asks the customer to wait, and notifies you (or the right team member) via SMS or email." },
        { q: "Is my customer data safe?", a: "Yes. All data is hosted in the EU and processed in a GDPR-compliant way. We never sell or share customer data." },
        { q: "Can I cancel anytime?", a: "The 1-month pilot is fully flexible. 6 and 12-month plans run for the agreed term in exchange for better pricing and onboarding — we'll recommend what fits during the free analysis." },
        { q: "Do you integrate with my booking system or POS?", a: "On the Pro plan we integrate with Google Calendar, Calendly, most Dutch POS systems and reservation platforms. Ask us about your specific stack." },
        { q: "What if I don't like the AI's tone?", a: "You approve every training pass. Tone, style and boundaries are configured with you before we go live — and can be adjusted anytime." },
      ],
    },
    dash: {
      eyebrow: "Live Demo Dashboard",
      title: "Live Demo: See How Recovered Leads Can Be Tracked",
      sub: "Your branded control room — metrics, conversations, pipeline and revenue in one place.",
      disclaimer: "Demo dashboard using example data. Real client data is kept private to protect our customers.",
      missed: "Missed Calls Recovered",
      leads: "Leads Recovered",
      conv: "Conversations Handled",
      apps: "Appointments Booked",
      revenue: "Revenue Recovered (Est.)",
      openDash: "Open Live Demo Dashboard",
    },
    liveDash: {
      disclaimerTag: "DEMO · EXAMPLE DATA",
      revLabel: "EXAMPLE · REVENUE RECOVERED · LAST 14 DAYS",
      weeklyLabel: "EXAMPLE · WEEKLY ACTIVITY",
      bookedCount: "62 booked",
      pipelineTitle: "Example Pipeline",
      pipelineTag: "DEMO · EXAMPLE LEADS ONLY",
      dragNote: "Drag any card between columns. All names shown are placeholder labels — not real customers.",
      days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      cards: {
        e1: { name: "Restaurant Lead", job: "Table for 4 · Fri 20:30", ago: "2m ago" },
        e2: { name: "Service Request", job: "Outlet sparking", ago: "8m ago" },
        e3: { name: "Example Customer", job: "Locked out — car key", ago: "15m" },
        e4: { name: "Customer Reply", job: "AC not cooling", ago: "22m" },
        e5: { name: "Plumbing Lead", job: "Kitchen pipe leak", ago: "45m" },
        e6: { name: "Booking Request", job: "Boiler service", ago: "1h" },
        e7: { name: "Restaurant Lead", job: "Group booking · 8 guests", ago: "1h" },
        e8: { name: "Service Request", job: "Emergency plumbing", ago: "3h" },
        e9: { name: "Example Customer", job: "Decided not to proceed", ago: "2d" },
      },
    },
    calc: {
      eyebrow: "Missed Call ROI Calculator",
      title: "Estimate How Much Missed Calls Could Be Costing You",
      sub: "Use this simple calculator to estimate the potential monthly value of missed calls in your business.",
      currencyLabel: "Currency",
      missedCalls: "How many calls do you miss per week?",
      avgValue: "What is one new customer worth to your business?",
      conv: "What percentage of missed callers could become customers?",
      helper: "Customer value differs per business. A restaurant booking may be worth €50–€100, while a plumber or service job can be worth €250–€750+.",
      monthly: "Estimated Monthly Lost Revenue",
      yearly: "Estimated Yearly Lost Revenue",
      disclaimer: "This is an estimate. Actual results depend on call volume, customer value, conversion rate and follow-up speed.",
      ctaTitle: "Recover missed calls with SMS + AI.",
      nextStep: "Next step",
      bookDemo: "Plan a Free Analysis",
    },
    kanban: {
      eyebrow: "CRM Pipeline",
      title: "AI-Qualified Leads, Drag-and-Drop Pipeline",
      sub: "Move leads through stages or let the AI do it for you.",
      stages: { new: "New Lead", contacted: "Contacted", qualified: "Qualified", booked: "Appointment Booked", won: "Won", lost: "Lost" },
    },
    services: {
      eyebrow: "What We Build",
      title: "Automation Systems That Pay for Themselves",
      list: [
        { title: "Missed-Call Recovery", desc: "Auto-follow up every missed caller with a personalized SMS within seconds." },
        { title: "AI Customer Replies", desc: "Trained assistant handles menu, hours, FAQs, prices and general questions 24/7." },
        { title: "Lead Intake", desc: "Structured intake: job type, location, urgency, reservation details — captured automatically." },
        { title: "Booking / Demo Flow", desc: "AI collects preferences and hands off to your calendar or booking system." },
        { title: "Human Takeover", desc: "Custom rules alert you or the right team member the moment something matters." },
        { title: "Follow-up Automation", desc: "Automatic reminders, re-engagement messages and review requests." },
        { title: "Reporting Dashboard", desc: "Track missed calls recovered, leads captured, appointments and revenue over time." },
      ],
      mostRequested: "Most-requested service",
    },
    cta: {
      title: "Ready to recover more missed opportunities?",
      sub: "Plan a free 30–45 minute analysis and see how our system helps recover missed leads and automate customer replies.",
      btn: "Plan a Free Analysis",
      btn2: "Contact Us",
    },
    contact: {
      eyebrow: "Get In Touch",
      title: "Book Your Free Strategy Call",
      sub: "Tell us about your business — we'll show you how much revenue may be recoverable.",
      formTitle: "Plan a Free Analysis",
      contactTitle: "Contact Us",
      name: "Full name", email: "Email", phone: "Phone", company: "Company", businessType: "Business Type", missedCalls: "Missed calls/week", date: "Preferred date", message: "Tell us about your situation",
      submitDemo: "Request Free Analysis",
      messageTitle: "Or send us a message directly",
      messageSub: "No calendar needed — fill in the form and we'll reply within one business day.",
      submitContact: "Send Message",
      sending: "Sending…",
      success: "Thanks — we'll be in touch within 24h.",
      error: "Something went wrong. Please try again.",
      info: { company: "Company", email: "Email", website: "Website", location: "Location", phone: "Phone" },
      callerIdTitle: "Received a call from TP Agency?",
      callerIdNote: "Our outbound calls may come from",
    },
    footer: {
      tagline: "AI missed-call recovery & customer support for local businesses.",
      services: "Services",
      company: "Company",
      contactHeading: "Contact",
      trustedBy: "Trusted by",
      privacy: "Privacy Policy",
      terms: "Terms and Conditions",
      rights: "All rights reserved.",
    },

    home: {
      problemTitle: "Missed calls = missed leads",
      servicesGlanceTitle: "Services at a glance",
      learnMore: "Learn more",
      howGlanceTitle: "How it works",
      howGlanceSteps: [
        { n: "01", title: "A call is missed", desc: "A customer calls while your team is busy or closed." },
        { n: "02", title: "The customer receives an immediate response", desc: "An automatic SMS goes out within seconds." },
        { n: "03", title: "The request is captured and forwarded", desc: "Details are collected and sent to your team for follow-up." },
      ],
      problemBody: "Missed calls often turn into lost opportunities because customers contact the next business that responds faster.",
      solutionTitle: "Reply instantly. Miss fewer opportunities.",
      solutionBody: "TP Agency helps you reply instantly with SMS + AI, collect customer details, and keep every lead organized in one place.",
      benefitsTitle: "Three ways TP Agency helps your business",
      benefits: [
        { title: "Reply faster", desc: "Automatic SMS replies within seconds of a missed call — no matter the time." },
        { title: "Capture more leads", desc: "AI collects the details you need — reservation, job type, location, urgency." },
        { title: "Stay organized", desc: "Every conversation and lead ends up in a clean pipeline your team can act on." },
      ],
      packagesTitle: "Packages that grow with your business",
      packagesSub: "From simple missed-call SMS to a full AI front desk. Flexible options — see the full breakdown on our pricing page.",
      packagesShort: [
        { name: "Basic", price: "From €199/mo", desc: "Missed-call SMS + AI assistant for opening hours and simple questions." },
        { name: "Pro", price: "From €299/mo", desc: "AI receptionist: reservations, job intake, human takeover and notifications.", featured: true },
        { name: "Premium", price: "From €449/mo", desc: "Full AI front desk with reporting, follow-ups and advanced automations." },
      ],
      seePricing: "See full pricing",
      seeServices: "Explore services",
      seeHow: "See how it works",
    },

    whoFor: {
      title: "Who This Is For",
      body: "TP Agency is built for businesses that receive customer calls, messages, bookings, or service requests and do not want missed opportunities to slip away.",
      items: [
        "Restaurants",
        "Plumbers and home services",
        "Barbers and salons",
        "Local service businesses",
        "Businesses that depend on fast customer response",
      ],
    },

    humanTakeover: {
      title: "Human Takeover When It Matters",
      body: "The AI can answer common questions and collect details, but your team stays in control.",
      subtitle: "Human takeover can be triggered for:",
      items: ["Complaints", "Urgent requests", "Allergy or safety questions", "Large bookings", "Pricing questions", "Anything the AI is unsure about"],
    },

    builtAround: {
      title: "Built Around Your Business",
      body: "Every setup is tailored to your business information, customer flow, opening hours, services, FAQs, tone of voice, and follow-up process.",
      body2: "We can use your website, Google Business profile, social media, menus, service pages, and approved business information to configure the system.",
    },

    afterBooking: {
      title: "What Happens After You Book",
      steps: [
        { n: "01", title: "Fill in a short business form", desc: "You share a few details about your business — takes 2 minutes." },
        { n: "02", title: "We review your setup", desc: "We look at your business, customer flow and current missed-call situation." },
        { n: "03", title: "Plan a free 30–45 minute analysis call", desc: "Pick a time that works for you from the calendar." },
        { n: "04", title: "See SMS + AI in action", desc: "We show exactly how the automation could work for your business." },
        { n: "05", title: "Right setup recommendation", desc: "If it's a good fit, we recommend the package that matches your needs." },
      ],
    },

    homeFaq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Do you replace my team?", a: "No. TP Agency helps your team respond faster, collect details, and stay organized. Human takeover is always possible." },
        { q: "Can the AI confirm bookings?", a: "Only if your business wants that and the setup allows it. Otherwise, the AI collects the request and your team confirms." },
        { q: "Does this work outside business hours?", a: "Yes. The system can reply when your team is busy, closed, or unable to answer." },
        { q: "Can this work for my industry?", a: "If your business receives calls, bookings, messages, or service requests, we can usually adapt the system to your workflow." },
        { q: "Is this a guarantee of more revenue?", a: "No. Results depend on call volume, response speed, customer value, and follow-up." },
      ],
    },

    builtByTP: {
      title: "Built by TP Agency",
      body: "We help local businesses turn missed calls and unanswered messages into organized follow-up opportunities using SMS, AI, and simple automation systems.",
      focus: "Our focus is simple: faster replies, better lead capture, and less chaos for busy teams.",
    },

    privacyExtra: "We do not show private customer conversations, phone numbers, or client data publicly.",

    howPage: {
      title: "How TP Agency works",
      sub: "A simple five-step flow that runs in the background — you keep working while your leads get replied to and tracked.",
      steps: [
        { n: "01", title: "Missed call or customer message", desc: "A call is missed, or a customer sends a message to your business — we detect it instantly." },
        { n: "02", title: "Instant SMS response", desc: "An automatic SMS is sent within seconds. Your caller feels acknowledged before they move on to a competitor." },
        { n: "03", title: "AI collects details or answers questions", desc: "The assistant answers common questions (hours, menu, services) and collects the details you need — job type, address, reservation time." },
        { n: "04", title: "Your team gets notified", desc: "Human-takeover rules alert the right person by SMS or email when a booked reservation, urgent job or important message comes in." },
        { n: "05", title: "Lead is tracked in the system", desc: "Every conversation and contact ends up in a clean pipeline so nothing falls through the cracks." },
      ],
    },

    industriesPage: {
      title: "Built around how you actually work",
      sub: "TP Agency adapts to your industry's customer flow, tone of voice and the details that matter for your business.",
      list: [
        { title: "Restaurants", desc: "Guide guests to the reservation page, share the menu, collect allergy information and capture special requests — even during service.", items: ["Reservation intake", "Menu & specials", "Opening hours", "Allergy notes", "Reservation guidance"] },
        { title: "Plumbers & home services", desc: "Qualify jobs, capture urgency and location, and get emergency calls on your calendar fast.", items: ["Urgency detection", "Location-based intake", "Job qualification", "Priority routing", "Emergency handling"] },
        { title: "Barbers & salons", desc: "Answer common questions, share prices, and turn missed calls into confirmed appointments.", items: ["Appointment intake", "Service list & prices", "Opening hours", "Reminders", "Recurring bookings"] },
        { title: "Local service businesses", desc: "For everything else — electricians, HVAC, locksmiths, tutors, cleaners and more.", items: ["Custom knowledge base", "Job or lead intake", "Follow-ups", "Reviews automation", "Owner notifications"] },
      ],
    },

    aboutPage: {
      title: "About TP Agency",
      lead: "TP Agency helps businesses respond faster and prevent missed calls from becoming lost customers.",
      brandAlt: "Original TP Agency logo",
      brandLabel: "The original mark",
      brandNote: "One brand, two marks: the original TP Agency logo we started with, and the eagle that now fronts our AI platform as mascot and icon.",
      paragraphs: [
        "TP Agency was built around a simple problem many businesses recognize: customers call, send messages or make requests, but busy teams cannot always respond right away. In that short waiting time, a valuable opportunity can easily be lost.",
        "We build systems that help businesses respond faster, follow up missed calls, and keep customer conversations organized. Not as a replacement for your team, but as support that brings more structure, speed and clarity to daily customer communication.",
        "Our AI assistants collect the right details, answer common questions and prepare important conversations for your team. When a personal response is the better option, your team stays in control and can take over at the right moment.",
        "Every setup is tailored to the way your business already works: your customer flow, tone of voice, opening hours, services, FAQs and follow-up rules. That way, the system does not feel like a generic chatbot, but like an extension of your business.",
        "TP Agency focuses on Dutch and European businesses that rely on fast customer response — from restaurants and salons to plumbers, electricians and multi-location service teams.",
      ],
      values: [
        { title: "Practical", desc: "No buzzwords. We build systems that pay for themselves." },
        { title: "Tailored", desc: "Every setup is configured for your business type and customer flow." },
        { title: "Human when it matters", desc: "AI handles the easy parts. You get notified the moment something important happens." },
      ],
    },

    pricingPage: {
      title: "Pricing that scales with your business",
      sub: "Simple monthly pricing. Save with 6 or 12-month plans. Packages can be adjusted depending on your business needs.",
      billingLabels: { monthly: "Monthly", six: "6 months", twelve: "12 months" },
      saveTag: { six: "Save 5%", twelve: "Save 10%" },
      addOnsTitle: "Add-ons",
      addOnsSub: "Extend any package based on how your business grows.",
      addOns: [
        { title: "Additional language", desc: "German, French, Spanish or Italian on top of NL/EN." },
        { title: "Extra call routing contacts", desc: "Route calls to more team members before the AI takes over." },
        { title: "Custom integrations", desc: "Calendar, POS, reservation platform or CRM of your choice." },
        { title: "Multi-location setup", desc: "Manage several branches under one dashboard." },
        { title: "Custom reporting", desc: "Weekly or monthly reports built around your KPIs." },
      ],
      adjustNote: "All packages can be adjusted based on your business size, call volume and specific needs. Not sure which one fits? Plan a free analysis — we'll recommend the right setup.",
    },
    privacyPage: {
      title: "Privacy Policy",
      updated: "Last updated: June 2026",
      intro: "This Privacy Policy explains how TP Agency (KVK: 95001751), based in the Netherlands, handles personal data collected through this website.",
      sections: [
        { h: "Data we collect", body: [
          "When you book a demo or contact us through our lead form, we may collect your name, email address, phone number, company name and the information you share in your message.",
          "We may also process limited technical data, such as browser type and pages visited, to keep the website working properly.",
        ] },
        { h: "How we use your data", body: [
          "We use your data to respond to your request, schedule and prepare demos, provide our services and improve our website and communication.",
          "We do not sell personal data and we do not use it for third-party advertising.",
        ] },
        { h: "Legal basis", body: [
          "We process personal data based on your consent (when you submit a form), the performance of an agreement, or our legitimate interest in responding to business enquiries.",
        ] },
        { h: "Sharing with third parties", body: [
          "We only share data with trusted service providers that help us run our website, communication and scheduling tools. These providers process data on our behalf and in line with applicable law.",
        ] },
        { h: "Data retention", body: [
          "We keep personal data no longer than necessary for the purpose it was collected for, or as required by law.",
        ] },
        { h: "Your rights", body: [
          "Under the GDPR you have the right to access, correct, delete or transfer your personal data, and to object to or restrict its processing. To exercise these rights, contact us at tp.agency@outlook.com.",
        ] },
        { h: "Contact", body: [
          "Questions about this policy? Contact TP Agency at tp.agency@outlook.com or +31 970 102 50680.",
        ] },
      ],
    },
    termsPage: {
      title: "Terms and Conditions",
      updated: "Last updated: June 2026",
      intro: "These Terms and Conditions apply to the website and services of TP Agency (KVK: 95001751), based in the Netherlands.",
      sections: [
        { h: "Our services", body: [
          "TP Agency provides missed-call recovery, AI-assisted customer communication and related automation services for businesses. The exact scope of each engagement is agreed per client.",
        ] },
        { h: "Quotes and pricing", body: [
          "Prices shown on this website are \"from\" prices, exclusive of VAT. The final price depends on business type, call volume and the complexity of the setup, and is confirmed in a written quote or agreement.",
        ] },
        { h: "Demos and communication", body: [
          "Booking a demo is free and does not create any obligation to purchase. Information shared during a demo is treated confidentially.",
        ] },
        { h: "Client responsibilities", body: [
          "Clients are responsible for providing accurate business information used to configure their setup, and for reviewing and approving automated messages before going live.",
        ] },
        { h: "No guarantees", body: [
          "TP Agency helps businesses respond faster and follow up missed leads. Results depend on factors outside our control, such as call volume and customer behaviour, and are therefore not guaranteed.",
        ] },
        { h: "Liability", body: [
          "To the extent permitted by law, TP Agency is not liable for indirect damages. Any liability is limited to the amount paid for the services in the month in which the event occurred.",
        ] },
        { h: "Term and termination", body: [
          "Subscription terms and notice periods are agreed per client in the service agreement.",
        ] },
        { h: "Governing law", body: [
          "Dutch law applies to these terms. Disputes will be submitted to the competent court in the Netherlands.",
        ] },
        { h: "Contact", body: [
          "Questions about these terms? Contact TP Agency at tp.agency@outlook.com or +31 970 102 50680.",
        ] },
      ],
    },
    admin: {
      loginTitle: "Admin Login",
      email: "Email", password: "Password", signIn: "Sign in",
      panelTitle: "Admin Panel",
      siteSettings: "Site Settings", leads: "Leads",
      save: "Save Changes", saved: "Settings saved.",
      logout: "Logout",
    },
  },

  nl: {
    nav: { home: "Home", services: "Diensten", how: "Hoe het werkt", pricing: "Prijzen", industries: "Sectoren", about: "Over ons", dashboard: "Live demo-dashboard", contact: "Contact", bookDemo: "Plan een gratis analyse" },
    navMenus: {
      services: [
        { label: "Gemiste-oproep herstel", to: "/nl/diensten#missed-call-recovery" },
        { label: "AI klantantwoorden", to: "/nl/diensten#ai-customer-replies" },
        { label: "Lead intake", to: "/nl/diensten#lead-intake" },
        { label: "Boekings- & reserveringsflow", to: "/nl/diensten#booking-flow" },
        { label: "Menselijke overname", to: "/nl/diensten#human-takeover" },
      ],
      industries: [
        { label: "Restaurants", to: "/nl/sectoren#restaurants" },
        { label: "Loodgieters & thuisservice", to: "/nl/sectoren#plumbers-home-services" },
        { label: "Kappers & salons", to: "/nl/sectoren#barbers-salons" },
        { label: "Lokale dienstverleners", to: "/nl/sectoren#local-service-businesses" },
      ],
    },
    clients: { title: "Vertrouwd door bedrijven die iedere lead belangrijk vinden" },
    mascot: { text: "Gemiste oproepen kunnen gemiste klanten betekenen. Ontdek wat uw bedrijf kan terugwinnen." },
    og: {
      title: "TP Agency | Voorkom klantverlies door gemiste oproepen",
      desc: "Directe SMS-opvolging en AI-ondersteunde klantenservice voor dienstverlenende bedrijven.",
    },
    notFound: {
      title: "Pagina niet gevonden | TP Agency",
      heading: "Pagina niet gevonden",
      sub: "Deze pagina bestaat niet of is verplaatst.",
      back: "Terug naar home",
    },
    seo: {
      home: { title: "TP Agency — AI-herstel van gemiste oproepen voor lokale bedrijven", desc: "TP Agency helpt lokale bedrijven binnen seconden te reageren op gemiste oproepen met SMS en AI, zodat minder leads naar concurrenten gaan. Plan een gratis analyse." },
      services: { title: "Diensten — Gemiste-oproep herstel, AI-antwoorden & lead intake | TP Agency", desc: "Gemiste-oproep herstel, AI-klantantwoorden, lead intake, boekingsflows en menselijke overname — automatisering gebouwd rond uw bedrijf." },
      how: { title: "Hoe het werkt — Gemiste-oproep herstel in 5 stappen | TP Agency", desc: "Zie hoe TP Agency gemiste oproepen detecteert, binnen seconden per SMS reageert, details verzamelt met AI en uw team waarschuwt wanneer het telt." },
      pricing: { title: "Prijzen — Pakketten voor AI gemiste-oproep herstel | TP Agency", desc: "Eenvoudige maandpakketten vanaf €199. Basic gemiste-oproep herstel, Pro AI-receptioniste en Premium volledige AI front desk. Prijzen excl. BTW." },
      industries: { title: "Sectoren — Restaurants, loodgieters, salons & meer | TP Agency", desc: "TP Agency past zich aan aan restaurants, loodgieters, kappers, salons en lokale dienstverleners — afgestemd op uw klantstroom en tone-of-voice." },
      about: { title: "Over TP Agency — Sneller reageren voor lokale bedrijven", desc: "TP Agency helpt bedrijven sneller te reageren en te voorkomen dat gemiste oproepen verloren klanten worden. Lees meer over onze aanpak." },
      contact: { title: "Contact TP Agency — Plan een gratis analyse", desc: "Plan een gratis analyse of stuur ons een bericht. We laten zien hoeveel omzet mogelijk te herstellen valt uit gemiste oproepen." },
      dashboard: { title: "Live demo-dashboard | TP Agency", desc: "Bekijk een demo-dashboard met voorbeelddata: herstelde oproepen, leads, gesprekken, afspraken en geschatte omzet." },
      privacy: { title: "Privacybeleid | TP Agency", desc: "Hoe TP Agency omgaat met persoonsgegevens die via deze website worden verzameld, conform de AVG." },
      terms: { title: "Algemene voorwaarden | TP Agency", desc: "De algemene voorwaarden die gelden voor de website en diensten van TP Agency." },
    },
    hero: {
      eyebrow: "AI Klantenservice · Gemiste-Oproep Herstel",
      title1: "Voorkom dat u klanten verliest",
      title2: "door gemiste oproepen",
      sub: "TP Agency helpt bedrijven sneller reageren, gemiste leads herstellen en klantantwoorden automatiseren met SMS- en AI-systemen op maat van uw bedrijfsstroom.",
      cta1: "Plan een gratis analyse",
      cta2: "Neem contact op",
      trust: "Gebouwd voor restaurants, loodgieters, elektriciens, HVAC en dienstverleners.",
      flow: {
        missed: "Gemiste oproep", missedSub: "+31 6 12 34 56 78 · 14:02",
        ai: "AI-sms verstuurd", aiSub: "Hallo! We hebben uw oproep gemist — waarmee kunnen we helpen?",
        reply: "Reactie van klant", replySub: "Gesprongen leiding in de keuken — spoed nodig",
        booked: "Afspraakverzoek vastgelegd", bookedSub: "Vandaag · 15:30 · Spoedservice",
        time: "Tijd tot herstel",
      },
    },
    problem: {
      eyebrow: "Het Probleem",
      title: "Elke gemiste oproep is een klant die naar uw concurrent gaat",
      sub: "Lokale bedrijven verliezen elke maand duizenden euro's simpelweg omdat de telefoon niet op tijd werd opgenomen.",
      points: [
        { stat: "62%", label: "van de bellers spreekt geen voicemail in — ze bellen het volgende bedrijf op Google." },
        { stat: "8 sec", label: "is alles wat de gemiddelde klant wacht op een reactie." },
        { stat: "€3.000+", label: "aan gemiste maandomzet voor een typisch lokaal dienstenbedrijf." },
        { stat: "24/7", label: "verwachten klanten nu een antwoord — ook om 23u op zondag." },
      ],
    },
    how: {
      eyebrow: "Hoe Het Werkt",
      title: "Een eenvoudig, betrouwbaar systeem dat op de achtergrond draait",
      sub: "U blijft uw werk doen. Wij helpen gemiste leads op te volgen en klantvragen te beantwoorden.",
      stepLabel: "STAP",
      steps: [
        { n: "01", title: "Gemiste oproep gedetecteerd", desc: "Zodra er een oproep wordt gemist, verstuurt ons systeem binnen seconden een automatische SMS naar de beller." },
        { n: "02", title: "AI-gesprek begint", desc: "Uw AI-assistent — op maat gemaakt voor uw bedrijf en klantstroom — begroet de klant en begrijpt wat zij nodig hebben." },
        { n: "03", title: "Vragen beantwoord · info verzameld", desc: "Openingstijden, menu, prijzen, dienstdetails, reserveringen, klusdetails — direct afgehandeld." },
        { n: "04", title: "U krijgt bericht als het telt", desc: "Human-takeover regels waarschuwen u alleen voor geboekte klussen, reserveringen of urgente leads." },
      ],
    },
    ai: {
      eyebrow: "AI Mogelijkheden",
      title: "Wat uw AI-assistent kan beantwoorden",
      sub: "Getraind op de details van uw bedrijf, klaar binnen dagen, altijd beleefd en on-brand.",
      list: [
        { title: "Bedrijfsinformatie", desc: "Over uw bedrijf, team, werkgebied, verhaal en USP's." },
        { title: "Openingstijden", desc: "Tijden van vandaag, feestdagen, spoedbeschikbaarheid." },
        { title: "Menu & Diensten", desc: "Gerechten, specials, dienstenlijst, prijzen, huidige promoties." },
        { title: "Reserveringen & Boekingen", desc: "Verzamelt reservering- en afspraakgegevens volgens uw regels." },
        { title: "FAQ", desc: "Elke terugkerende vraag van klanten, direct beantwoord." },
        { title: "Nuttige Links", desc: "Menu-PDF's, bezorg-, bestel-, boek- of betaallinks op verzoek." },
        { title: "Allergie & Veiligheid", desc: "Allergenen, veiligheidsprocedures, verzekering, garanties." },
        { title: "Klus- & Lead-intake", desc: "Adres, urgentie, probleembeschrijving, foto's — direct naar u doorgestuurd." },
        { title: "Menselijke overname", desc: "Op maat gemaakte regels bepalen wanneer wordt geëscaleerd naar u of uw team." },
      ],
    },
    packages: {
      eyebrow: "Pakketten",
      title: "Prijs op basis van serviceniveau, niet van bedrijfstype",
      sub: "Sommige bedrijven hebben alleen basis-gemiste-oproep-herstel nodig. Andere hebben geavanceerde AI lead-intake, reserveringen, klus-kwalificatie, human-takeover regels en rapportage nodig. Alle pakketten hanteren \"Vanaf\"-prijzen — de eindprijs hangt af van de complexiteit.",
      billing: "per maand",
      fromLabel: "Vanaf",
      vat: "excl. BTW",
      cta: "Plan een gratis analyse",
      list: [
        {
          name: "Basic — Gemiste-oproep herstel",
          price: "€199",
          badge: null,
          bestFor: "Restaurants, kappers, salons en kleine lokale bedrijven die eenvoudige missed-call opvolging nodig hebben.",
          desc: "Voor bedrijven die automatisch willen antwoorden bij een gemiste oproep en klanten willen helpen met basisvragen.",
          features: [
            "Gemiste-oproep SMS",
            "Basis AI SMS-assistent",
            "Openingstijden",
            "Algemene bedrijfsvragen",
            "Basis FAQ",
            "1 telefoonnummer",
            "Eenvoudige lead-inbox",
            "Basis setup en testen",
          ],
        },
        {
          name: "Pro — AI Receptioniste / Lead Intake",
          price: "€299",
          badge: "Meest gekozen",
          bestFor: "Restaurants met reserveringen en loodgieters/dienstverleners die meer gedetailleerde klant-intake nodig hebben.",
          desc: "Voor bedrijven die AI vragen willen laten beantwoorden, reserveringsverzoeken of klusgegevens willen laten verzamelen, en de eigenaar willen laten waarschuwen wanneer menselijke opvolging nodig is.",
          features: [
            "Alles uit Basic",
            "Geavanceerde AI kennisbank",
            "Menu- of diensten-vragen",
            "Reserverings- of klus-intake vragen",
            "Human-takeover regels",
            "Eigenaar-notificaties",
            "Pijplijn-setup",
            "Basis agenda- / aanvraag-setup",
            "Maandelijkse optimalisatie",
          ],
        },
        {
          name: "Premium — Volledige AI Front Desk",
          price: "€449",
          badge: null,
          bestFor: "Drukke restaurants, loodgieters en dienstverleners met een hoger leadvolume of complexere workflows.",
          desc: "Voor bedrijven die een completer AI front-desk-systeem willen met rapportage, opvolging en geavanceerde automatiseringen.",
          features: [
            "Alles uit Pro",
            "Geavanceerde workflows",
            "Review-automatisering",
            "Rapportage-dashboard",
            "Opvolg-automatisering",
            "Meerdere AI-flows",
            "Voorrangs-ondersteuning",
            "Maandelijkse performance-review",
          ],
        },
      ],
      setup: "Prijzen worden weergegeven in EUR en zijn exclusief BTW. TP Agency richt zich momenteel op Nederlandse en Europese bedrijven.",
      note: "De prijs is afhankelijk van het type bedrijf, oproepvolume, SMS-gebruik, AI-complexiteit en het gewenste automatiseringsniveau. Loodgieters en dienstverleners hebben meestal lead-kwalificatie, urgentie-detectie en locatie-intake nodig — deze zijn vanaf het Pro-pakket inbegrepen.",
    },
    restoDemo: {
      eyebrow: "Restaurantdemo",
      title: "Zo werkt het voor een restaurant",
      sub: "Echt vrijdagavond-scenario — de telefoon gaat terwijl het team gasten bedient.",
      businessName: "Trattoria Bella · Amsterdam",
      messages: [
        { from: "system", text: "📞 Gemiste oproep van +31 6 21 88 44 12 · 19:34" },
        { from: "ai", t: "19:34", text: "Hallo! Sorry dat we niet konden opnemen — dit is Bella's AI-assistent. Waarmee kan ik helpen?" },
        { from: "user", t: "19:34", text: "Hebben jullie vanavond een tafel voor 4 rond 20:30?" },
        { from: "ai", t: "19:34", text: "Ik geef uw reserveringsverzoek voor 4 personen om 20:30 graag door aan het team. Allergieën of dieetwensen om te noteren?" },
        { from: "user", t: "19:35", text: "Eén vegetariër, één glutenvrij graag." },
        { from: "ai", t: "19:35", text: "Genoteerd. Mag ik de naam voor het verzoek en een mobiel nummer zodat het team kan bevestigen?" },
        { from: "user", t: "19:35", text: "Julia — 06 21 88 44 12" },
        { from: "ai", t: "19:36", text: "✅ Verzoek vastgelegd, Julia — vrijdag 20:30 · 4 gasten · veg + GV. Het team bevestigt snel per SMS!" },
      ],
    },
    plumberDemo: {
      eyebrow: "Loodgietersdemo",
      title: "Zo werkt het voor een loodgieter",
      sub: "Spoedoproep hersteld in minder dan een minuut — klusintake automatisch afgehandeld.",
      businessName: "PipePro Loodgieters · Rotterdam",
      messages: [
        { from: "system", t: "14:02", text: "📞 Gemiste oproep van +31 6 12 34 56 78" },
        { from: "ai", t: "14:02", text: "Hallo! Dit is Mike van PipePro Loodgieters. Sorry dat we uw oproep misten — wat is er aan de hand?" },
        { from: "user", t: "14:03", text: "Ik heb een gesprongen leiding onder mijn gootsteen, overal water!" },
        { from: "ai", t: "14:03", text: "Dat klinkt urgent. Kunt u de hoofdkraan dichtdraaien? En wat is uw postcode?" },
        { from: "user", t: "14:04", text: "Ja, die staat dicht. Postcode 1011AB Amsterdam." },
        { from: "ai", t: "14:04", text: "Genoteerd. Ik stuur dit als spoedverzoek door naar Mike. Heeft u vandaag een voorkeurstijd?" },
        { from: "user", t: "14:05", text: "Zo snel mogelijk — begin van de middag komt goed uit." },
        { from: "ai", t: "14:05", text: "✅ Spoedverzoek doorgestuurd naar Mike — voorkeurstijd: begin van de middag. Hij bevestigt de beschikbaarheid snel per SMS. Houd het veilig." },
      ],
    },
    demo: {
      step1: "Gemiste oproep", step2: "AI-sms verstuurd", step3: "Klant reageert", step4: "AI kwalificeert", step5: "Verzoek doorgestuurd",
      aiStatus: "AI-assistent · antwoordt automatisch",
    },
    why: {
      eyebrow: "Waarom TP Agency",
      title: "Gebouwd voor bedrijven die sneller willen reageren",
      sub: "Eenvoudige systemen, heldere workflows en AI-automatisering die uw team helpt gemiste leads te vangen, sneller te reageren, georganiseerd te blijven en voorbereid te zijn op elk klantcontact. Flexibele opties, afhankelijk van uw bedrijfsbehoeften. Duidelijke pakketten, eenvoudige onboarding en support dat past bij de fase van uw bedrijf.",
      points: [
        { title: "In dagen live, niet maanden", desc: "We trainen uw AI op uw bedrijf, gaan snel live en verfijnen samen met u." },
        { title: "Op maat van uw bedrijf", desc: "Geen generieke chatbot — elk antwoord is ingesteld op uw specifieke bedrijfstype en klantstroom." },
        { title: "Menselijke overname als het telt", desc: "U bepaalt precies wanneer de AI een echte persoon moet inschakelen." },
        { title: "Werkt terwijl u slaapt", desc: "24/7 antwoorden op gemiste oproepen en berichten, ook 's avonds en in het weekend." },
        { title: "GDPR-vriendelijk & NL-first", desc: "EU-hosted, GDPR-conform, native Nederlands en Engels." },
        { title: "Verdient zichzelf terug", desc: "Één herstelde klus dekt meestal een volledige maand abonnement." },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Antwoorden op de meest gestelde vragen",
      list: [
        { q: "Hoe lang duurt de setup?", a: "De meeste bedrijven zijn binnen 3–7 werkdagen live. Wij verzorgen de training met uw info, menu of dienstenlijst en koppelen dit aan uw telefoonnummer." },
        { q: "Moet ik mijn telefoonnummer wijzigen?", a: "Nee. We houden uw bestaande nummer. Gemiste oproepen worden gedetecteerd via doorschakeling of het missed-call-event van uw telecomprovider." },
        { q: "Welke talen spreekt de AI?", a: "Native Nederlands en Engels standaard. Duits, Frans, Spaans en Italiaans beschikbaar op het Pro-pakket." },
        { q: "Wat gebeurt er als de AI het antwoord niet weet?", a: "Ze volgt uw human-takeover regels: ze vraagt de klant beleefd om even te wachten en verwittigt u via SMS of e-mail." },
        { q: "Zijn mijn klantgegevens veilig?", a: "Ja. Alle data wordt in de EU gehost en GDPR-conform verwerkt. We verkopen of delen nooit klantgegevens." },
        { q: "Kan ik altijd opzeggen?", a: "De 1-maand pilot is volledig flexibel. 6- en 12-maandenplannen lopen voor de afgesproken termijn in ruil voor betere prijzen en onboarding — tijdens de gratis analyse adviseren we wat het beste past." },
        { q: "Integreren jullie met mijn boekingssysteem of POS?", a: "Op het Pro-pakket integreren we met Google Calendar, Calendly, de meeste NL POS-systemen en reserveringsplatforms." },
        { q: "Wat als ik de toon van de AI niet leuk vind?", a: "U keurt elke trainingsronde goed. Toon, stijl en grenzen worden samen met u ingesteld — en altijd aan te passen." },
      ],
    },
    dash: {
      eyebrow: "Live demo-dashboard",
      title: "Live demo: zo kunnen herstelde leads worden bijgehouden",
      sub: "Uw eigen controlecentrum — statistieken, gesprekken, pijplijn en omzet op één plek.",
      disclaimer: "Demo-dashboard met voorbeeldgegevens. Echte klantgegevens houden we privé om onze klanten te beschermen.",
      missed: "Gemiste oproepen hersteld",
      leads: "Leads hersteld",
      conv: "Gesprekken afgehandeld",
      apps: "Afspraken geboekt",
      revenue: "Omzet hersteld (geschat)",
      openDash: "Open live demo-dashboard",
    },
    liveDash: {
      disclaimerTag: "DEMO · VOORBEELDDATA",
      revLabel: "VOORBEELD · HERSTELDE OMZET · LAATSTE 14 DAGEN",
      weeklyLabel: "VOORBEELD · WEEKACTIVITEIT",
      bookedCount: "62 geboekt",
      pipelineTitle: "Voorbeeld-pijplijn",
      pipelineTag: "DEMO · ALLEEN VOORBEELDLEADS",
      dragNote: "Sleep een kaart tussen kolommen. Alle namen zijn voorbeeldlabels — geen echte klanten.",
      days: ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"],
      cards: {
        e1: { name: "Restaurant-lead", job: "Tafel voor 4 · vr 20:30", ago: "2 min" },
        e2: { name: "Serviceverzoek", job: "Stopcontact vonkt", ago: "8 min" },
        e3: { name: "Voorbeeldklant", job: "Buitengesloten — autosleutel", ago: "15 min" },
        e4: { name: "Klantreactie", job: "Airco koelt niet", ago: "22 min" },
        e5: { name: "Loodgieter-lead", job: "Lekkende keukenleiding", ago: "45 min" },
        e6: { name: "Boekingsverzoek", job: "CV-ketel onderhoud", ago: "1 u" },
        e7: { name: "Restaurant-lead", job: "Groepsboeking · 8 gasten", ago: "1 u" },
        e8: { name: "Serviceverzoek", job: "Spoed loodgieterswerk", ago: "3 u" },
        e9: { name: "Voorbeeldklant", job: "Besloten niet door te gaan", ago: "2 d" },
      },
    },
    calc: {
      eyebrow: "Gemiste-oproep ROI Calculator",
      title: "Schat in hoeveel gemiste oproepen u kunnen kosten",
      sub: "Gebruik deze eenvoudige calculator om de mogelijke maandwaarde van gemiste oproepen in uw bedrijf te schatten.",
      currencyLabel: "Valuta",
      missedCalls: "Hoeveel oproepen mist u per week?",
      avgValue: "Wat is één nieuwe klant waard voor uw bedrijf?",
      conv: "Welk percentage van gemiste bellers kan klant worden?",
      helper: "Klantwaarde verschilt per bedrijf. Een restaurantboeking is vaak €50–€100 waard, terwijl een loodgieter- of dienstenklus €250–€750+ kan zijn.",
      monthly: "Geschat maandelijks omzetverlies",
      yearly: "Geschat jaarlijks omzetverlies",
      disclaimer: "Dit is een schatting. Werkelijke resultaten hangen af van oproepvolume, klantwaarde, conversie en opvolgsnelheid.",
      ctaTitle: "Herstel gemiste oproepen met SMS + AI.",
      nextStep: "Volgende stap",
      bookDemo: "Plan een gratis analyse",
    },
    kanban: {
      eyebrow: "CRM Pijplijn",
      title: "AI-gekwalificeerde leads, drag-and-drop pijplijn",
      sub: "Sleep leads door de fases of laat de AI het voor u doen.",
      stages: { new: "Nieuwe Lead", contacted: "Gecontacteerd", qualified: "Gekwalificeerd", booked: "Afspraak Geboekt", won: "Gewonnen", lost: "Verloren" },
    },
    services: {
      eyebrow: "Wat we bouwen",
      title: "Automatiseringssystemen die zichzelf terugverdienen",
      list: [
        { title: "Gemiste-Oproep Herstel", desc: "Automatische SMS-opvolging naar elke gemiste beller binnen seconden." },
        { title: "AI Klantantwoorden", desc: "Getrainde assistent beantwoordt menu, tijden, FAQ, prijzen en algemene vragen 24/7." },
        { title: "Lead Intake", desc: "Gestructureerde intake: klustype, locatie, urgentie, reserveringsdetails — automatisch vastgelegd." },
        { title: "Boeking / Demo Flow", desc: "AI verzamelt voorkeuren en zet door naar uw agenda of boekingssysteem." },
        { title: "Menselijke Overname", desc: "Regels op maat waarschuwen u of het juiste teamlid zodra het telt." },
        { title: "Opvolg-automatisering", desc: "Automatische herinneringen, heractivatie en review-verzoeken." },
        { title: "Rapportage-dashboard", desc: "Volg herstelde oproepen, gevangen leads, afspraken en omzet in de tijd." },
      ],
      mostRequested: "Meest gevraagde dienst",
    },
    cta: {
      title: "Klaar om minder klanten mis te lopen?",
      sub: "Plan een gratis analyse van 30–45 minuten en ontdek hoe ons systeem gemiste leads helpt herstellen en klantantwoorden automatiseert.",
      btn: "Plan een gratis analyse",
      btn2: "Neem contact op",
    },
    contact: {
      eyebrow: "Neem contact op",
      title: "Boek uw gratis strategiegesprek",
      sub: "Vertel ons over uw bedrijf — we laten zien hoeveel omzet mogelijk te herstellen valt.",
      formTitle: "Plan een gratis analyse",
      contactTitle: "Neem contact op",
      name: "Volledige naam", email: "E-mail", phone: "Telefoon", company: "Bedrijf", businessType: "Type bedrijf", missedCalls: "Gemiste oproepen/week", date: "Voorkeurdatum", message: "Vertel ons over uw situatie",
      submitDemo: "Gratis analyse aanvragen",
      messageTitle: "Of stuur ons direct een bericht",
      messageSub: "Geen agenda nodig — vul het formulier in en we reageren binnen één werkdag.",
      submitContact: "Bericht versturen",
      sending: "Versturen…",
      success: "Bedankt — we nemen binnen 24u contact op.",
      error: "Er ging iets mis. Probeer opnieuw.",
      info: { company: "Bedrijf", email: "E-mail", website: "Website", location: "Locatie", phone: "Telefoon" },
      callerIdTitle: "Gebeld door TP Agency?",
      callerIdNote: "Wij bellen uit via",
    },
    footer: {
      tagline: "AI gemiste-oproep herstel en klantenservice voor lokale bedrijven.",
      services: "Diensten",
      company: "Bedrijf",
      contactHeading: "Contact",
      trustedBy: "Vertrouwd door",
      privacy: "Privacybeleid",
      terms: "Algemene voorwaarden",
      rights: "Alle rechten voorbehouden.",
    },

    home: {
      problemTitle: "Gemiste oproepen = gemiste leads",
      servicesGlanceTitle: "Diensten in het kort",
      learnMore: "Meer informatie",
      howGlanceTitle: "Hoe het werkt",
      howGlanceSteps: [
        { n: "01", title: "Er wordt een oproep gemist", desc: "Een klant belt terwijl uw team bezig of gesloten is." },
        { n: "02", title: "De klant ontvangt direct een reactie", desc: "Binnen seconden gaat er automatisch een SMS uit." },
        { n: "03", title: "Het verzoek wordt vastgelegd en doorgestuurd", desc: "Gegevens worden verzameld en naar uw team gestuurd voor opvolging." },
      ],
      problemBody: "Gemiste oproepen worden vaak gemiste kansen — klanten bellen simpelweg het volgende bedrijf dat sneller reageert.",
      solutionTitle: "Direct reageren. Minder kansen laten liggen.",
      solutionBody: "TP Agency helpt u direct te reageren met SMS + AI, klantdetails te verzamelen en elke lead georganiseerd te houden.",
      benefitsTitle: "Drie manieren waarop TP Agency uw bedrijf helpt",
      benefits: [
        { title: "Sneller reageren", desc: "Automatische SMS-antwoorden binnen seconden na een gemiste oproep — dag en nacht." },
        { title: "Meer leads vangen", desc: "AI verzamelt de details die u nodig heeft — reservering, klustype, locatie, urgentie." },
        { title: "Georganiseerd blijven", desc: "Elk gesprek en elke lead in één overzichtelijke pijplijn." },
      ],
      packagesTitle: "Pakketten die met uw bedrijf meegroeien",
      packagesSub: "Van eenvoudige SMS-opvolging tot een volledige AI-frontdesk. Flexibele opties — zie het volledige overzicht op onze prijzenpagina.",
      packagesShort: [
        { name: "Basic", price: "Vanaf €199/mnd", desc: "Gemiste-oproep SMS + AI-assistent voor openingstijden en simpele vragen." },
        { name: "Pro", price: "Vanaf €299/mnd", desc: "AI receptioniste: reserveringen, klus-intake, human takeover en notificaties.", featured: true },
        { name: "Premium", price: "Vanaf €449/mnd", desc: "Volledige AI-frontdesk met rapportage, opvolging en geavanceerde automatisering." },
      ],
      seePricing: "Bekijk volledige prijzen",
      seeServices: "Bekijk diensten",
      seeHow: "Zo werkt het",
    },

    whoFor: {
      title: "Voor wie is dit",
      body: "TP Agency is gebouwd voor bedrijven die klantoproepen, berichten, boekingen of serviceverzoeken ontvangen en geen gemiste kansen willen laten glippen.",
      items: ["Restaurants", "Loodgieters en thuisservice", "Kappers en salons", "Lokale dienstverleners", "Bedrijven die afhangen van snelle klantreactie"],
    },
    humanTakeover: {
      title: "Menselijke overname wanneer het telt",
      body: "De AI kan veelgestelde vragen beantwoorden en details verzamelen, maar uw team blijft in controle.",
      subtitle: "Menselijke overname kan worden geactiveerd voor:",
      items: ["Klachten", "Spoedverzoeken", "Allergie- of veiligheidsvragen", "Grote boekingen", "Prijsvragen", "Alles waarvan de AI onzeker is"],
    },
    builtAround: {
      title: "Gebouwd rond uw bedrijf",
      body: "Elke setup is op maat gemaakt voor uw bedrijfsinformatie, klantstroom, openingstijden, diensten, FAQ, tone-of-voice en opvolgproces.",
      body2: "We kunnen uw website, Google Bedrijfsprofiel, social media, menu's, dienstenpagina's en goedgekeurde bedrijfsinformatie gebruiken om het systeem te configureren.",
    },
    afterBooking: {
      title: "Wat gebeurt er nadat u boekt",
      steps: [
        { n: "01", title: "Vul een kort bedrijfsformulier in", desc: "U deelt enkele details over uw bedrijf — duurt 2 minuten." },
        { n: "02", title: "Wij bekijken uw setup", desc: "We kijken naar uw bedrijf, klantstroom en huidige gemiste-oproep situatie." },
        { n: "03", title: "Plan een gratis analyse van 30–45 minuten", desc: "Kies een tijd die u past uit de agenda." },
        { n: "04", title: "Zie SMS + AI in actie", desc: "We laten precies zien hoe de automatisering voor uw bedrijf kan werken." },
        { n: "05", title: "Juist advies voor uw setup", desc: "Als het past, adviseren we het pakket dat het beste bij uw behoeften past." },
      ],
    },
    homeFaq: {
      title: "Veelgestelde vragen",
      items: [
        { q: "Vervangt u mijn team?", a: "Nee. TP Agency helpt uw team sneller te reageren, details te verzamelen en georganiseerd te blijven. Menselijke overname is altijd mogelijk." },
        { q: "Kan de AI boekingen bevestigen?", a: "Alleen als uw bedrijf dat wil en de setup het toestaat. Anders verzamelt de AI het verzoek en bevestigt uw team." },
        { q: "Werkt dit buiten kantooruren?", a: "Ja. Het systeem kan antwoorden wanneer uw team druk is, gesloten is of niet kan opnemen." },
        { q: "Kan dit voor mijn sector werken?", a: "Als uw bedrijf oproepen, boekingen, berichten of serviceverzoeken ontvangt, kunnen we het systeem meestal aanpassen aan uw workflow." },
        { q: "Is dit een garantie op meer omzet?", a: "Nee. Resultaten hangen af van oproepvolume, reactiesnelheid, klantwaarde en opvolging." },
      ],
    },
    builtByTP: {
      title: "Gebouwd door TP Agency",
      body: "We helpen lokale bedrijven gemiste oproepen en onbeantwoorde berichten om te zetten in georganiseerde opvolgkansen met SMS, AI en eenvoudige automatiseringssystemen.",
      focus: "Onze focus is eenvoudig: snellere antwoorden, betere lead-capture en minder chaos voor drukke teams.",
    },
    privacyExtra: "We tonen geen privé klantgesprekken, telefoonnummers of klantgegevens publiekelijk.",


    howPage: {
      title: "Zo werkt TP Agency",
      sub: "Een eenvoudig 5-stappen-proces dat op de achtergrond draait — u blijft doorwerken terwijl uw leads worden beantwoord en bijgehouden.",
      steps: [
        { n: "01", title: "Gemiste oproep of klantbericht", desc: "Er wordt een oproep gemist of een klant stuurt een bericht — wij detecteren dit direct." },
        { n: "02", title: "Directe SMS-reactie", desc: "Binnen seconden gaat er automatisch een SMS uit. De klant voelt zich gehoord voor ze naar een concurrent gaan." },
        { n: "03", title: "AI verzamelt details of beantwoordt vragen", desc: "De assistent beantwoordt veelgestelde vragen en verzamelt de nodige details — klustype, adres, reserveringstijd." },
        { n: "04", title: "Uw team krijgt bericht", desc: "Human-takeover regels waarschuwen de juiste persoon via SMS of e-mail bij een boeking, spoedklus of belangrijk bericht." },
        { n: "05", title: "Lead wordt bijgehouden", desc: "Elk gesprek en contact komt in een overzichtelijke pijplijn zodat niets tussen wal en schip valt." },
      ],
    },

    industriesPage: {
      title: "Afgestemd op uw klantproces",
      sub: "TP Agency stemt het systeem af op uw klantstroom, tone-of-voice en sectorspecifieke details.",
      list: [
        { title: "Restaurants", desc: "Wijs gasten naar de reserveringspagina, deel het menu, verzamel allergie-informatie en leg speciale verzoeken vast — zelfs tijdens de service.", items: ["Reservering-intake", "Menu & specials", "Openingstijden", "Allergie-notities", "Reserveringsbegeleiding"] },
        { title: "Loodgieters & thuisservice", desc: "Kwalificeer klussen, leg urgentie en locatie vast, en krijg spoedoproepen snel in uw agenda.", items: ["Urgentiedetectie", "Locatie-intake", "Kluskwalificatie", "Prioriteit-routing", "Spoedafhandeling"] },
        { title: "Kappers & salons", desc: "Beantwoord veelgestelde vragen, deel prijzen, en zet gemiste oproepen om in bevestigde afspraken.", items: ["Afsprakenintake", "Dienstenlijst & prijzen", "Openingstijden", "Herinneringen", "Terugkerende boekingen"] },
        { title: "Lokale dienstverleners", desc: "Voor al het andere — elektriciens, HVAC, slotenmakers, docenten, schoonmakers en meer.", items: ["Custom kennisbank", "Klus- of lead-intake", "Opvolging", "Review-automatisering", "Eigenaar-notificaties"] },
      ],
    },

    aboutPage: {
      title: "Over TP Agency",
      lead: "TP Agency helpt bedrijven sneller te reageren en te voorkomen dat gemiste oproepen verloren kansen worden.",
      brandAlt: "Origineel TP Agency-logo",
      brandLabel: "Het originele beeldmerk",
      brandNote: "Eén merk, twee beeldmerken: het originele TP Agency-logo waarmee we begonnen, en de adelaar die nu als mascotte en icoon ons AI-platform vertegenwoordigt.",
      paragraphs: [
        "TP Agency is ontstaan vanuit een simpel probleem dat veel bedrijven herkennen: klanten bellen, sturen berichten of doen aanvragen, maar drukke teams kunnen niet altijd direct reageren. En in die korte wachttijd kan een waardevolle kans verloren gaan.",
        "Wij bouwen systemen die bedrijven helpen sneller te reageren, gemiste oproepen beter op te volgen en klantvragen overzichtelijk te verwerken. Niet als vervanging van uw team, maar als ondersteuning die rust, structuur en snelheid brengt in het dagelijkse klantcontact.",
        "Onze AI-assistenten verzamelen de juiste gegevens, beantwoorden veelgestelde vragen en zorgen dat belangrijke gesprekken netjes worden klaargezet voor uw team. Wanneer persoonlijk contact beter past, blijft menselijke opvolging altijd onderdeel van het proces.",
        "Elke setup wordt afgestemd op de manier waarop uw bedrijf werkt: uw klantstroom, tone-of-voice, openingstijden, diensten, veelgestelde vragen en opvolgregels. Zo voelt het systeem niet als een standaard chatbot, maar als een verlengstuk van uw bedrijf.",
        "TP Agency richt zich op Nederlandse en Europese bedrijven die afhankelijk zijn van snelle klantreacties — van restaurants en salons tot loodgieters, elektriciens en servicebedrijven met meerdere locaties.",
      ],
      values: [
        { title: "Praktisch", desc: "Geen buzzwords. We bouwen systemen die zichzelf terugverdienen." },
        { title: "Op maat", desc: "Elke setup is geconfigureerd voor uw bedrijfstype en klantstroom." },
        { title: "Mens wanneer het telt", desc: "AI handelt het makkelijke af. U krijgt bericht zodra er iets belangrijks gebeurt." },
      ],
    },

    pricingPage: {
      title: "Prijzen die meegroeien met uw bedrijf",
      sub: "Eenvoudige maandprijzen. Bespaar met 6- of 12-maanden pakketten. Pakketten kunnen worden aangepast aan uw bedrijfsbehoeften.",
      billingLabels: { monthly: "Maandelijks", six: "6 maanden", twelve: "12 maanden" },
      saveTag: { six: "5% korting", twelve: "10% korting" },
      addOnsTitle: "Add-ons",
      addOnsSub: "Breid elk pakket uit naarmate uw bedrijf groeit.",
      addOns: [
        { title: "Extra taal", desc: "Duits, Frans, Spaans of Italiaans naast NL/EN." },
        { title: "Extra doorschakel-contacten", desc: "Route naar meer teamleden voordat de AI overneemt." },
        { title: "Custom integraties", desc: "Agenda, POS, reserveringsplatform of CRM naar keuze." },
        { title: "Multi-locatie setup", desc: "Beheer meerdere vestigingen onder één dashboard." },
        { title: "Custom rapportage", desc: "Week- of maandrapporten rond uw KPI's." },
      ],
      adjustNote: "Alle pakketten kunnen worden aangepast op basis van uw bedrijfsgrootte, oproepvolume en specifieke wensen. Twijfelt u welk pakket past? Plan een gratis analyse — we adviseren u graag.",
    },

    privacyPage: {
      title: "Privacybeleid",
      updated: "Laatst bijgewerkt: juni 2026",
      intro: "Dit privacybeleid legt uit hoe TP Agency (KVK: 95001751), gevestigd in Nederland, omgaat met persoonsgegevens die via deze website worden verzameld.",
      sections: [
        { h: "Gegevens die we verzamelen", body: [
          "Wanneer u een demo boekt of contact met ons opneemt via ons formulier, kunnen we uw naam, e-mailadres, telefoonnummer, bedrijfsnaam en de informatie in uw bericht verzamelen.",
          "We kunnen ook beperkte technische gegevens verwerken, zoals browsertype en bezochte pagina's, om de website goed te laten werken.",
        ] },
        { h: "Hoe we uw gegevens gebruiken", body: [
          "We gebruiken uw gegevens om te reageren op uw aanvraag, demo's in te plannen en voor te bereiden, onze diensten te leveren en onze website en communicatie te verbeteren.",
          "We verkopen geen persoonsgegevens en gebruiken ze niet voor advertenties van derden.",
        ] },
        { h: "Rechtsgrond", body: [
          "We verwerken persoonsgegevens op basis van uw toestemming (wanneer u een formulier verstuurt), de uitvoering van een overeenkomst, of ons gerechtvaardigd belang om te reageren op zakelijke aanvragen.",
        ] },
        { h: "Delen met derden", body: [
          "We delen gegevens alleen met vertrouwde dienstverleners die ons helpen bij het beheren van onze website, communicatie en planningstools. Deze partijen verwerken gegevens namens ons en volgens de geldende wetgeving.",
        ] },
        { h: "Bewaartermijn", body: [
          "We bewaren persoonsgegevens niet langer dan nodig is voor het doel waarvoor ze zijn verzameld, of zolang de wet dat vereist.",
        ] },
        { h: "Uw rechten", body: [
          "Onder de AVG heeft u het recht om uw persoonsgegevens in te zien, te corrigeren, te verwijderen of over te dragen, en om bezwaar te maken tegen of beperking te vragen van de verwerking. Neem hiervoor contact op via tp.agency@outlook.com.",
        ] },
        { h: "Contact", body: [
          "Vragen over dit beleid? Neem contact op met TP Agency via tp.agency@outlook.com of +31 970 102 50680.",
        ] },
      ],
    },
    termsPage: {
      title: "Algemene voorwaarden",
      updated: "Laatst bijgewerkt: juni 2026",
      intro: "Deze algemene voorwaarden zijn van toepassing op de website en diensten van TP Agency (KVK: 95001751), gevestigd in Nederland.",
      sections: [
        { h: "Onze diensten", body: [
          "TP Agency levert gemiste-oproep herstel, AI-ondersteunde klantcommunicatie en gerelateerde automatiseringsdiensten voor bedrijven. De exacte omvang van elke samenwerking wordt per klant afgesproken.",
        ] },
        { h: "Offertes en prijzen", body: [
          "Prijzen op deze website zijn \"vanaf\"-prijzen, exclusief BTW. De uiteindelijke prijs hangt af van het type bedrijf, het oproepvolume en de complexiteit van de setup, en wordt bevestigd in een schriftelijke offerte of overeenkomst.",
        ] },
        { h: "Demo's en communicatie", body: [
          "Het boeken van een demo is gratis en verplicht u tot niets. Informatie die tijdens een demo wordt gedeeld, wordt vertrouwelijk behandeld.",
        ] },
        { h: "Verantwoordelijkheden van de klant", body: [
          "Klanten zijn verantwoordelijk voor het aanleveren van juiste bedrijfsinformatie voor de configuratie van hun setup, en voor het beoordelen en goedkeuren van geautomatiseerde berichten voordat deze live gaan.",
        ] },
        { h: "Geen garanties", body: [
          "TP Agency helpt bedrijven sneller te reageren en gemiste leads op te volgen. Resultaten hangen af van factoren buiten onze controle, zoals oproepvolume en klantgedrag, en worden daarom niet gegarandeerd.",
        ] },
        { h: "Aansprakelijkheid", body: [
          "Voor zover wettelijk toegestaan is TP Agency niet aansprakelijk voor indirecte schade. Eventuele aansprakelijkheid is beperkt tot het bedrag dat is betaald voor de diensten in de maand waarin de gebeurtenis plaatsvond.",
        ] },
        { h: "Looptijd en opzegging", body: [
          "Abonnementstermijnen en opzegtermijnen worden per klant vastgelegd in de dienstverleningsovereenkomst.",
        ] },
        { h: "Toepasselijk recht", body: [
          "Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.",
        ] },
        { h: "Contact", body: [
          "Vragen over deze voorwaarden? Neem contact op met TP Agency via tp.agency@outlook.com of +31 970 102 50680.",
        ] },
      ],
    },
    admin: {
      loginTitle: "Beheer Login",
      email: "E-mail", password: "Wachtwoord", signIn: "Inloggen",
      panelTitle: "Beheerpaneel",
      siteSettings: "Site Instellingen", leads: "Leads",
      save: "Wijzigingen Opslaan", saved: "Instellingen opgeslagen.",
      logout: "Uitloggen",
    },
  },
};

const I18nContext = createContext({ lang: "en", t: dict.en, setLang: () => {}, prefix: "", lp: (p) => p });

export const I18nProvider = ({ children }) => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const isNl = pathname === "/nl" || pathname.startsWith("/nl/");
  const lang = isNl ? "nl" : "en";
  const t = dict[lang];
  const prefix = isNl ? "/nl" : "";
  const lp = (enPath) => (isNl ? toNlPath(enPath) : enPath);
  const setLang = (l) => {
    if ((l === "nl") === isNl) return;
    const enBase = isNl ? toEnPath(pathname) : pathname;
    navigate((l === "nl" ? toNlPath(enBase) : enBase) + hash);
  };
  return <I18nContext.Provider value={{ lang, t, setLang, prefix, lp }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
